import type { ClinicianInboxTagRule, QueueItem } from "@/types/clinician";

export type QueuePriorityTagTone =
  | "neutral"
  | "accent"
  | "success"
  | "warning"
  | "danger"
  | "info";

export interface QueuePriorityTag {
  key: string;
  label: string;
  tone: QueuePriorityTagTone;
}

const CATEGORY_TAGS: Record<string, QueuePriorityTag> = {
  adverse_effect: {
    key: "adverse-effect",
    label: "Adverse effect",
    tone: "danger",
  },
  dosing: { key: "dosing", label: "Dosing", tone: "accent" },
  interaction: {
    key: "interaction",
    label: "Drug interaction",
    tone: "danger",
  },
  treatment_response: {
    key: "treatment-response",
    label: "Treatment response",
    tone: "info",
  },
  prescription: {
    key: "prescription",
    label: "Prescription",
    tone: "accent",
  },
  administration: {
    key: "administration",
    label: "Administration",
    tone: "accent",
  },
  adherence: { key: "adherence", label: "Adherence", tone: "warning" },
  intake: { key: "intake", label: "Intake review", tone: "info" },
  follow_up: { key: "follow-up", label: "Follow-up", tone: "info" },
  pregnancy: { key: "pregnancy", label: "Pregnancy", tone: "danger" },
  clinical_handoff: {
    key: "clinical-handoff",
    label: "Clinical handoff",
    tone: "warning",
  },
  human_requested: {
    key: "human-requested",
    label: "Human requested",
    tone: "warning",
  },
  ai_paused: {
    key: "ai-paused",
    label: "AI replies paused",
    tone: "warning",
  },
};

const REASON_TAGS: Array<QueuePriorityTag & { pattern: RegExp }> = [
  {
    key: "adverse-effect",
    label: "Adverse effect",
    tone: "danger",
    pattern:
      /\b(adverse|side effects?|reaction|redness|red|swollen|swelling|rash|headaches?|nausea|fatigue|feeling sick)\b/i,
  },
  {
    key: "interaction",
    label: "Drug interaction",
    tone: "danger",
    pattern: /\b(interaction|contraindication)\b/i,
  },
  {
    key: "dose-discrepancy",
    label: "Dose discrepancy",
    tone: "warning",
    pattern:
      /(?:\b(dose|dosage|strength|units?|prescription)\b.*\b(differs?|discrepancy|mismatch|wrong|changed)\b)|(?:\b(differs?|discrepancy|mismatch|wrong|changed)\b.*\b(dose|dosage|strength|units?|prescription)\b)/i,
  },
  {
    key: "treatment-blocked",
    label: "Treatment blocked",
    tone: "warning",
    pattern:
      /\b(treatment|dose|dosing|dispensing|medication|prescription|script)\b.{0,40}\b(blocked|pending|cannot|can't|unable|waiting)\b|\b(blocked|cannot|can't|unable)\b.{0,40}\b(treatment|dose|dosing|dispensing|medication|prescription|script)\b/i,
  },
  {
    key: "follow-up",
    label: "Follow-up",
    tone: "info",
    pattern:
      /\b(follow(?:ing)? up|unanswered|no response|still waiting|awaiting)\b/i,
  },
  {
    key: "billing",
    label: "Billing / cancellation",
    tone: "info",
    pattern: /\b(refund|charge|charged|billing|bank|subscription|cancel)\b/i,
  },
  {
    key: "order-issue",
    label: "Order issue",
    tone: "info",
    pattern: /\b(order|shipment|shipping|tracking|delivered|pharmacy)\b/i,
  },
  {
    key: "appointment",
    label: "Appointment",
    tone: "info",
    pattern: /\b(appointment|visit|call|reschedul|no-show|missed zoom)\b/i,
  },
  {
    key: "document-review",
    label: "Document review",
    tone: "accent",
    pattern: /\b(document|attachment|blood work|lab work|photo|image)\b/i,
  },
];

function addTag(
  tags: QueuePriorityTag[],
  seen: Set<string>,
  tag: QueuePriorityTag,
) {
  if (seen.has(tag.key)) return;
  seen.add(tag.key);
  tags.push(tag);
}

/**
 * Turn the structured queue category and AI situation summary into a small,
 * stable set of scannable labels. Status badges (overdue, reopened, etc.) are
 * deliberately handled by the row itself and do not belong here.
 */
export function queuePriorityTags(
  item: QueueItem,
  definitions: ClinicianInboxTagRule[] = [],
): QueuePriorityTag[] {
  const structuredTags = Array.isArray(item.tags) ? item.tags : [];
  if (structuredTags.length > 0) {
    const byKey = new Map(
      definitions.map((definition) => [
        definition.key.toLowerCase(),
        definition,
      ]),
    );
    const tags: QueuePriorityTag[] = [];
    const seen = new Set<string>();
    for (const rawKey of structuredTags) {
      const key = String(rawKey).trim().toLowerCase();
      if (
        !key ||
        key === "ai_ready" ||
        key === "ai-ready" ||
        key === "triage_error" ||
        key === "triage-error"
      )
        continue;
      if (key === "ai_paused" && item.ai_replies_paused === false) continue;
      const definition = byKey.get(key);
      // Safety already has a dedicated, higher-salience badge and queue. Do
      // not render a second copy of the same signal on an urgent row.
      if (
        (definition?.kind === "safety" || key === "safety") &&
        item.urgency === 3
      )
        continue;
      const priority = definition?.priority ?? item.tag_priority ?? 1;
      addTag(tags, seen, {
        key,
        label: definition?.label ?? labelizeTagKey(key),
        tone:
          definition?.kind === "safety"
            ? "danger"
            : priority === 3
              ? "warning"
              : priority === 2
                ? "accent"
                : "neutral",
      });
    }
    return tags;
  }

  const tags: QueuePriorityTag[] = [];
  const seen = new Set<string>();
  const category = String(item.category || "").toLowerCase();
  const reason = item.urgency_reason || "";

  const categoryTag =
    category === "ai_paused" && item.ai_replies_paused === false
      ? undefined
      : CATEGORY_TAGS[category];
  if (categoryTag) addTag(tags, seen, categoryTag);

  for (const tag of REASON_TAGS) {
    if (!tag.pattern.test(reason)) continue;
    addTag(tags, seen, tag);
    if (tags.length >= 3) break;
  }

  if (tags.length === 0 && item.needs_clinician) {
    addTag(tags, seen, {
      key: "clinical-review",
      label: "Clinical review",
      tone: "neutral",
    });
  }

  return tags.slice(0, 3);
}

function labelizeTagKey(value: string): string {
  return value
    .replaceAll("-", " ")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
